/**
 * GPS Data Provider Factory Functions
 * Provides convenient factory functions for creating pre-configured GPS Data Providers
 */

import { GpsDataProvider } from './GpsDataProvider';
import type { GpsDataProviderConfig } from './types';

/**
 * Create a basic GPS Data Provider with default configuration
 * Note: Uses AuthStore for authentication automatically
 * @returns Configured GPS Data Provider
 */
export function createBasicGpsProvider(): GpsDataProvider {
  const provider = new GpsDataProvider({
    updateThreshold: {
      position: 0.00005, // ~5m (더 민감하게)
      speed: 1,         // 1km/h
      interval: 500     // 500ms (더 자주)
    },
    enableCaching: true,
    cacheSize: 100,
    autoFilter: {
      excludeInactive: false,
      excludeInvalidCoords: true,
      excludeStatic: false,
      minSpeedThreshold: 0.5
    },
    maxDevicesPerUpdate: 50,
    enableBatching: true,
    batchInterval: 2000
  });
  
  // Provider 생성만 하고, WebSocket 연결은 별도로 호출해야 함
  provider.initialize(); // WebSocket 연결 없이 Provider만 초기화
  
  return provider;
}

/**
 * Create an optimized GPS Data Provider for real-time map applications
 * Note: Uses AuthStore for authentication automatically
 * @returns Map-optimized GPS Data Provider
 */
export function createMapGpsProvider(): GpsDataProvider {
  const provider = new GpsDataProvider({
    updateThreshold: {
      position: 0.00005, // ~5m (more sensitive for maps)
      speed: 0.5,        // 0.5km/h (more sensitive)
      interval: 500      // 500ms (faster updates)
    },
    enableCaching: true,
    cacheSize: 200,
    autoFilter: {
      excludeInactive: false,
      excludeInvalidCoords: true,
      excludeStatic: false,
      minSpeedThreshold: 0
    },
    maxDevicesPerUpdate: 100,
    enableBatching: false,  // Disable batching for real-time responsiveness
    batchInterval: 1000
  });
  
  // Provider 생성만 하고, WebSocket 연결은 별도로 호출해야 함
  provider.initialize(); // WebSocket 연결 없이 Provider만 초기화
  
  return provider;
}

/**
 * Create a high-performance GPS Data Provider for dashboard applications
 * Note: Uses AuthStore for authentication automatically
 * @returns Dashboard-optimized GPS Data Provider
 */
export function createDashboardGpsProvider(): GpsDataProvider {
  const provider = new GpsDataProvider({
    updateThreshold: {
      position: 0.0001, // ~10m
      speed: 2,         // 2km/h (less sensitive for dashboard)
      interval: 2000    // 2s (less frequent updates)
    },
    enableCaching: true,
    cacheSize: 500,   // Larger cache for dashboard
    autoFilter: {
      excludeInactive: true,  // Focus on active devices
      excludeInvalidCoords: true,
      excludeStatic: true,    // Exclude static devices
      minSpeedThreshold: 1
    },
    maxDevicesPerUpdate: 200,
    enableBatching: true,
    batchInterval: 3000
  });
  
  // Provider 생성만 하고, WebSocket 연결은 별도로 호출해야 함
  provider.initialize(); // WebSocket 연결 없이 Provider만 초기화
  
  return provider;
}

/**
 * Create a zone-based GPS Data Provider for geographic area monitoring
 * Note: Uses AuthStore for authentication automatically
 * @returns Zone-optimized GPS Data Provider
 */
export function createZoneGpsProvider(): GpsDataProvider {
  const provider = new GpsDataProvider({
    updateThreshold: {
      position: 0.0002, // ~20m (less sensitive for zone monitoring)
      speed: 2,         // 2km/h
      interval: 3000    // 3s (less frequent for zone monitoring)
    },
    enableCaching: true,
    cacheSize: 300,    // Larger cache for zone data
    autoFilter: {
      excludeInactive: false,
      excludeInvalidCoords: true,
      excludeStatic: false,
      minSpeedThreshold: 0
    },
    maxDevicesPerUpdate: 200,  // More devices for zone monitoring
    enableBatching: true,
    batchInterval: 5000        // Longer batch interval
  });
  
  // Provider 생성만 하고, WebSocket 연결은 별도로 호출해야 함
  provider.initialize(); // WebSocket 연결 없이 Provider만 초기화
  
  return provider;
}

/**
 * Create a no-auth GPS Data Provider for public device monitoring
 * Note: Authentication handled automatically by GPS WebSocket SDK via AuthStore
 * @returns Public-optimized GPS Data Provider
 */
export function createPublicGpsProvider(): GpsDataProvider {
  const provider = new GpsDataProvider({
    updateThreshold: {
      position: 0.0005, // ~50m (less sensitive for public data)
      speed: 5,         // 5km/h
      interval: 5000    // 5s (less frequent updates)
    },
    enableCaching: true,
    cacheSize: 50,     // Smaller cache for public data
    autoFilter: {
      excludeInactive: true,  // Only show active public devices
      excludeInvalidCoords: true,
      excludeStatic: true,    // Exclude static devices
      minSpeedThreshold: 2
    },
    maxDevicesPerUpdate: 100,
    enableBatching: true,
    batchInterval: 10000       // 10s batch interval
  });
  
  // Provider 생성만 하고, WebSocket 연결은 별도로 호출해야 함
  provider.initialize(); // WebSocket 연결 없이 Provider만 초기화
  
  return provider;
}